import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { PHARMACY_ROUTES } from "./path";
import { isMasterDataAdmin } from "../Services/warehouseContext";
import Login from "../Components/Login";
import CommonLayout from "../Components/Layout/commonLayout";
import Dashboard from "../Components/DashBoard/Dashboard";
import CheOrders from "../Components/Orders/CheEngage/CheOrders";
import B2cOrders from "../Components/Orders/B2cOrders/B2cOrders";
// B2B Partners screens disabled — leave imports commented out so the modules
// still exist and can be reactivated by uncommenting both the import and the
// matching <Route> below.
// import B2bOrders from "../Components/Orders/B2bOrders/B2bOrders";
import EnquiryReport from "../Components/Reports/EnquiryReport";
import OrderReport from "../Components/Reports/OrderReport";
import ConsignmentReport from "../Components/Reports/ConsignmentReport";
import ProfileSideBar from "../Components/CustomerDetails/Patients/ProfileSidebar";
// import B2bProfileSibebar from "../Components/CustomerDetails/B2B/B2bProfileSibebar";
// import B2BKycReport from "../Components/CustomerDetails/B2B/B2BKycReport";
import CheEngage from "../Components/Reports/OrderReports/CheEngageOrderReports";
// import B2BOrderReport from "../Components/Reports/OrderReports/B2BOrderReports";
import B2COrderReports from "../Components/Reports/OrderReports/B2COrderReports";
import EyewearOrderReports from "../Components/Reports/OrderReports/EyewearOrderReports";
import CheEngageConsignmentReport from "../Components/Reports/ConsignmentReport/CheEngageConsignmentReport";
// import B2BConsignmentReport from "../Components/Reports/ConsignmentReport/B2BConsignmentReport";
import B2CConsignmentReport from "../Components/Reports/ConsignmentReport/B2CConsignmentReport";
import GrnTemplate from "../Components/POS/GrnTemplate";
import EyewearOrders from "../Components/Orders/EyewearOrders/EyewearOrders";
import Inventory from "../Components/Supply/Inventory";
import PurchaseOrder from "../Components/Supply/PurchaseOrder";
import Suppliers from "../Components/Supply/Suppliers";
import Doctors from "../Components/Supply/Doctors";
import SalesBillEntry from "../Components/Supply/SalesBillEntry";
import Grn from "../Components/Supply/Grn";
import PurchaseReturn from "../Components/Supply/Notes/DebitNote";
import PurchaseInvoice from "../Components/Supply/PurchaseInvoice";
import SalesReturn from "../Components/Supply/Notes/SalesReturn";
import AllocationBilling from "../Components/Supply/AllocationBilling";
// Pack & ship disabled for now — uncomment with the matching <Route> to restore.
// import PackShip from "../Components/Supply/PackShip";
import Billing from "../Components/Supply/Billing";
import ReportsOverview from "../Components/Supply/Reports/Overview";
import StockReport from "../Components/Supply/Reports/StockReport";
import StockBatchwise from "../Components/Supply/Reports/StockBatchwise";
import StockExpiry from "../Components/Supply/Reports/StockExpiry";
import StockLow from "../Components/Supply/Reports/StockLow";
import OutOfStuckReport from "../Components/Supply/Reports/OutOfStuckReport";
import StockSnapshot from "../Components/Supply/Reports/StockSnapshot";
import SalesReport from "../Components/Supply/Reports/SalesReport";
import SaleReportDatewise from "../Components/Supply/Reports/SaleReportDatewise";
import SalesRegister from "../Components/Supply/Reports/SalesRegister";
import SalesByChannel from "../Components/Supply/Reports/SalesByChannel";
import SalesOutstanding from "../Components/Supply/Reports/SalesOutstanding";
import PurchaseReport from "../Components/Supply/Reports/PurchaseReport";
import GRNReportDatewise from "../Components/Supply/Reports/GRNReportDatewise";
import PurchaseRegister from "../Components/Supply/Reports/PurchaseRegister";
import PurchaseBySupplier from "../Components/Supply/Reports/PurchaseBySupplier";
import PurchaseOrders from "../Components/Supply/Reports/PurchaseOrders";
import MedicineRequests from "../Components/Master/MedicineRequests";
import MedicineRequestApprovals from "../Components/Master/MedicineRequestApprovals";
import Racks from "../Components/Supply/Racks/Racks";

/* Every screen below the layout is behind the login. Without this, pasting a
   deep link such as /dashBoard rendered the full app to anyone who had the URL —
   the router happily mounted the layout and each screen fetched with whatever
   (or no) token was in localStorage.

   The session is the `access_token` Login.jsx writes; HttpCommonALL's 401
   interceptor clears it and sends the user back here when it expires mid-session.
   `from` is stashed so a future change can bounce the user back to the link they
   originally opened after signing in. */
const RequireAuth = ({ children }) => {
  const location = useLocation();
  const token = localStorage.getItem("access_token");
  if (!token) {
    return (
      <Navigate
        to={PHARMACY_ROUTES.LOGIN}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }
  return children;
};

/* Admin-only screens. Today that's the Medicine Request approval queue: a plain
   PH user may raise and track requests but not decide them, so a pasted deep
   link lands them back on their own requests screen instead of the queue. The
   sidebar hides the entry as well (see adminOnly in Layout/navConfig.js). */
const RequireAdmin = ({ children }) => {
  if (!isMasterDataAdmin()) {
    return <Navigate to={PHARMACY_ROUTES.MEDICINE_REQUESTS} replace />;
  }
  return children;
};

function OverallRoutes() {
  return (
    <Router>
      <Routes>
        <Route path={PHARMACY_ROUTES.LOGIN} element={<Login />} />

        <Route
          path="/"
          element={
            <RequireAuth>
              <CommonLayout />
            </RequireAuth>
          }
        >
          <Route path={PHARMACY_ROUTES.DASHBOARD} element={<Dashboard />} />
          <Route path={PHARMACY_ROUTES.CHE_ORDERS} element={<CheOrders />} />
          <Route path={PHARMACY_ROUTES.B2C_ORDERS} element={<B2cOrders />} />
          {/* B2B Partners screens disabled — uncomment with the matching import to restore. */}
          {/* <Route path={PHARMACY_ROUTES.B2B_ORDERS} element={<B2bOrders />} /> */}
          <Route path={PHARMACY_ROUTES.EYEWEAR_ORDERS} element={<EyewearOrders />} />

          <Route
            path={PHARMACY_ROUTES.ENQUIRY_REPORT}
            element={<EnquiryReport />}
          />
          <Route path={PHARMACY_ROUTES.ORDER_REPORT} element={<CheEngage />} />
          {/* B2B Partners screens disabled — uncomment with the matching import to restore. */}
          {/* <Route
            path={PHARMACY_ROUTES.B2B_ORDERSREPORT}
            element={<B2BOrderReport />}
          /> */}
          <Route
            path={PHARMACY_ROUTES.B2C_ORDERSREPORT}
            element={<B2COrderReports />}
          />
          <Route
            path={PHARMACY_ROUTES.EYEWEAR_ORDERSREPORT}
            element={<EyewearOrderReports />}
          />
          <Route
            path={PHARMACY_ROUTES.CHE_CONSIGNMENT_REPORT}
            element={<CheEngageConsignmentReport />}
          />
          {/* B2B Partners screens disabled — uncomment with the matching import to restore. */}
          {/* <Route
            path={PHARMACY_ROUTES.B2B_CONSIGNMENT_REPORT}
            element={<B2BConsignmentReport />}
          /> */}
          <Route
            path={PHARMACY_ROUTES.B2C_CONSIGNMENT_REPORT}
            element={<B2CConsignmentReport />}
          />
          <Route path={PHARMACY_ROUTES.PROFILE} element={<ProfileSideBar />} />

          {/* Master › Medicine requests — the requester's screen and the
              master-data team's approval queue for the same records. */}
          <Route
            path={PHARMACY_ROUTES.MEDICINE_REQUESTS}
            element={<MedicineRequests />}
          />
          <Route
            path={PHARMACY_ROUTES.MEDICINE_REQUEST_APPROVALS}
            element={
              <RequireAdmin>
                <MedicineRequestApprovals />
              </RequireAdmin>
            }
          />
          {/* Master › Racks — a store's shelves and the medicine → shelf map.
              Not admin-gated: the people who put stock away are the ones who need
              it, and the GRN already creates racks inline for any user. Scoped to
              the signed-in user's store (a head can switch). */}
          <Route path={PHARMACY_ROUTES.RACKS} element={<Racks />} />
          {/* B2B Partners screens disabled — uncomment with the matching imports to restore. */}
          {/* <Route
            path={PHARMACY_ROUTES.B2BPROFILE}
            element={<B2bProfileSibebar />}
          />
          <Route
            path={PHARMACY_ROUTES.B2B_KYCREPORT}
            element={<B2BKycReport />}
          /> */}
          <Route
            path={PHARMACY_ROUTES.GRN_TEMPLATE}
            element={<GrnTemplate />}
          />

          {/* Supply */}
          <Route path={PHARMACY_ROUTES.INVENTORY} element={<Inventory />} />
          <Route path={PHARMACY_ROUTES.PURCHASE_ORDERS} element={<PurchaseOrder />} />
          <Route path={PHARMACY_ROUTES.SUPPLIERS} element={<Suppliers />} />
          {/* GRN receive screen — reached from a PO's "Receive · create GRN" (no nav entry). */}
          <Route path={PHARMACY_ROUTES.GRN_INWARD} element={<Grn />} />
          <Route path={PHARMACY_ROUTES.PURCHASE_RETURN} element={<PurchaseReturn />} />
          <Route path={PHARMACY_ROUTES.PURCHASE_INVOICES} element={<PurchaseInvoice />} />
          <Route path={PHARMACY_ROUTES.SALES_BILL_ENTRY} element={<SalesBillEntry />} />
          <Route
            path={PHARMACY_ROUTES.ALLOCATION_BILLING}
            element={<AllocationBilling />}
          />
          {/* Pack & ship disabled for now — uncomment with the PACK_SHIP path to restore. */}
          {/* <Route path={PHARMACY_ROUTES.PACK_SHIP} element={<PackShip />} /> */}
          <Route path={PHARMACY_ROUTES.INVOICES} element={<Billing />} />
          <Route path={PHARMACY_ROUTES.SALES_RETURN} element={<SalesReturn />} />
          <Route path={PHARMACY_ROUTES.DOCTORS} element={<Doctors />} />

          {/* Supply › Reports — hubs */}
          <Route path={PHARMACY_ROUTES.REPORTS_OVERVIEW} element={<ReportsOverview />} />
          <Route path={PHARMACY_ROUTES.STOCK_REPORT} element={<StockReport />} />
          <Route path={PHARMACY_ROUTES.SALES_REPORT} element={<SalesReport />} />
          <Route path={PHARMACY_ROUTES.PURCHASE_REPORT} element={<PurchaseReport />} />
          {/* Stock sub-reports */}
          <Route path={PHARMACY_ROUTES.STOCK_REPORT_BATCHWISE} element={<StockBatchwise />} />
          <Route path={PHARMACY_ROUTES.STOCK_REPORT_EXPIRY} element={<StockExpiry />} />
          <Route path={PHARMACY_ROUTES.STOCK_REPORT_LOW} element={<StockLow />} />
          <Route path={PHARMACY_ROUTES.STOCK_REPORT_OUT_OF_STUCK} element={<OutOfStuckReport />} />
          <Route path={PHARMACY_ROUTES.STOCK_REPORT_SNAPSHOT} element={<StockSnapshot />} />
          {/* Sales sub-reports */}
          <Route path={PHARMACY_ROUTES.SALES_REPORT_DATEWISE} element={<SaleReportDatewise />} />
          <Route path={PHARMACY_ROUTES.SALES_REPORT_REGISTER} element={<SalesRegister />} />
          <Route path={PHARMACY_ROUTES.SALES_REPORT_CHANNEL} element={<SalesByChannel />} />
          <Route path={PHARMACY_ROUTES.SALES_REPORT_OUTSTANDING} element={<SalesOutstanding />} />
          {/* Purchase sub-reports */}
          <Route path={PHARMACY_ROUTES.PURCHASE_REPORT_GRN_DATEWISE} element={<GRNReportDatewise />} />
          <Route path={PHARMACY_ROUTES.PURCHASE_REPORT_REGISTER} element={<PurchaseRegister />} />
          <Route path={PHARMACY_ROUTES.PURCHASE_REPORT_SUPPLIER} element={<PurchaseBySupplier />} />
          <Route path={PHARMACY_ROUTES.PURCHASE_REPORT_ORDERS} element={<PurchaseOrders />} />
        </Route>

        {/* Anything unrecognised goes to the login rather than rendering a blank
            screen — an unknown deep link must not look like a signed-in app. */}
        <Route path="*" element={<Navigate to={PHARMACY_ROUTES.LOGIN} replace />} />
      </Routes>
    </Router>
  );
}

export default OverallRoutes;
